import { useEffect, useState } from "react";
import { Loader2, Wrench, AlertTriangle, CheckCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * R33 — Admin diagnostic page for RFQ Register orphans.
 *
 * An orphan is a Project / Standalone NPD / Assembly whose `from_rfq_id` is
 * either null or points to a soft-deleted RFQ. The startup migration heals
 * the backlog; this page surfaces any stragglers admins need to clean up by
 * hand.
 */
export default function RfqOrphans() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healing, setHealing] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/rfq-orphans");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load orphans: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const heal = async (kind, id) => {
    setHealing((prev) => ({ ...prev, [id]: true }));
    try {
      const r = await api.post(`/admin/rfq-orphans/heal/${kind}/${id}`);
      toast.success(`Healed — new RFQ ${r.data.rfq_id}`);
      await load();
    } catch (e) {
      toast.error("Heal failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setHealing((prev) => ({ ...prev, [id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Loading orphans…
      </div>
    );
  }
  if (!data) return null;

  const total = data.total || 0;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="rfq-orphans-page">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Wrench size={20} className="text-orange-600" />
          <h1 className="text-2xl font-bold text-slate-900">RFQ Register — Orphan Audit</h1>
        </div>
        <button
          onClick={load}
          className="text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"
          data-testid="orphans-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Projects, standalone NPDs, and assemblies missing a link to the RFQ Register.
        Each <strong>Heal</strong> creates a stub RFQ and links it back. Project heals also
        propagate the new <code>rfq_id</code> to every child NPD.
      </p>

      {total === 0 ? (
        <div
          className="rounded-md bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-2 text-emerald-800"
          data-testid="orphans-empty"
        >
          <CheckCircle size={18} /> No orphans found. The RFQ Register is fully healed.
        </div>
      ) : (
        <div className="rounded-md bg-rose-50 border border-rose-200 p-3 mb-4 flex items-center gap-2 text-rose-800">
          <AlertTriangle size={18} /> Found {total} orphan record{total === 1 ? "" : "s"}.
        </div>
      )}

      {["projects", "npds", "assemblies"].map((kind) => {
        const rows = data[kind] || [];
        if (!rows.length) return null;
        const singular = { projects: "project", npds: "npd", assemblies: "assembly" }[kind];
        return (
          <section key={kind} className="mb-6">
            <h2 className="font-semibold mb-2 capitalize text-slate-800">
              {kind} <span className="text-xs font-normal text-slate-400">({rows.length})</span>
            </h2>
            <table
              className="w-full text-sm border border-slate-200 rounded-md overflow-hidden"
              data-testid={`orphans-${kind}`}
            >
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Number</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const id = r.project_id || r.npd_id || r.assembly_id;
                  const number =
                    r.project_number ||
                    r.npd_number ||
                    r.assembly_number ||
                    id;
                  const name =
                    r.project_name ||
                    r.part_name ||
                    r.part_number ||
                    r.assembly_name ||
                    "—";
                  return (
                    <tr key={id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{number}</td>
                      <td className="px-3 py-2 text-slate-700">{name}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {(r.created_at || "").slice(0, 10)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-mono text-[10px]">
                          {r.reason}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => heal(singular, id)}
                          disabled={!!healing[id]}
                          className="px-2.5 py-1 text-xs rounded bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`heal-${singular}-${id}`}
                        >
                          {healing[id] ? (
                            <>
                              <Loader2 size={12} className="animate-spin" /> Healing…
                            </>
                          ) : (
                            <>
                              <Wrench size={12} /> Heal
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}
